/**
 * Time-ordered row slicing for Vybe Agent dataset previews and tool payloads.
 */

export type DatasetRow = Record<string, unknown>;

export type PreviewOrder = "tail" | "head";

export function resolveTimestampColumn(columns: string[]): string | undefined {
  if (columns.includes("timestampMs")) return "timestampMs";
  if (columns.includes("timestamp")) return "timestamp";
  if (columns.includes("ts")) return "ts";
  return undefined;
}

function rowTimestamp(row: DatasetRow, key: string): number | undefined {
  const v = row[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return undefined;
}

/** True when rows look chronologically ascending on the timestamp column. */
export function isTimeAscendingRows(
  rows: DatasetRow[],
  timestampKey: string,
): boolean {
  if (rows.length < 2) return true;
  const first = rowTimestamp(rows[0]!, timestampKey);
  const last = rowTimestamp(rows[rows.length - 1]!, timestampKey);
  if (first == null || last == null) return true;
  return first <= last;
}

export function sliceDatasetRows(
  rows: DatasetRow[],
  columns: string[],
  limit: number,
): {
  rows: DatasetRow[];
  previewOrder: PreviewOrder;
  firstTimestampMs?: number;
  lastTimestampMs?: number;
} {
  if (rows.length === 0) {
    return { rows: [], previewOrder: "head" };
  }

  const tsKey = resolveTimestampColumn(columns);
  const useTail = tsKey != null && isTimeAscendingRows(rows, tsKey);

  const previewOrder: PreviewOrder = useTail ? "tail" : "head";
  const sliced =
    rows.length <= limit
      ? rows
      : useTail
        ? rows.slice(-limit)
        : rows.slice(0, limit);

  let firstTimestampMs: number | undefined;
  let lastTimestampMs: number | undefined;
  if (tsKey) {
    for (const row of sliced) {
      const t = rowTimestamp(row, tsKey);
      if (t == null) continue;
      if (firstTimestampMs == null || t < firstTimestampMs) {
        firstTimestampMs = t;
      }
      if (lastTimestampMs == null || t > lastTimestampMs) {
        lastTimestampMs = t;
      }
    }
  }

  return { rows: sliced, previewOrder, firstTimestampMs, lastTimestampMs };
}

function tupleTimestamp(row: unknown, tsIndex: number): number | undefined {
  if (!Array.isArray(row)) return undefined;
  const v = row[tsIndex];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return undefined;
}

/** Cap row arrays for vybe_dsl tool output (objects or SQL tuples). */
export function capRowsForAgent<T>(
  rows: T[],
  cap: number,
  opts?: { columns?: string[] },
): {
  rows: T[];
  truncated: boolean;
  rowCount: number;
} {
  if (rows.length <= cap) {
    return { rows, truncated: false, rowCount: rows.length };
  }

  const first = rows[0];
  const last = rows[rows.length - 1];

  let useTail = false;
  if (
    first != null &&
    typeof first === "object" &&
    !Array.isArray(first) &&
    last != null &&
    typeof last === "object" &&
    !Array.isArray(last)
  ) {
    const cols = opts?.columns ?? [];
    const tsKey = resolveTimestampColumn(cols) ?? "timestampMs";
    if (tsKey in (first as DatasetRow)) {
      useTail = isTimeAscendingRows(rows as DatasetRow[], tsKey);
    }
  } else if (Array.isArray(first) && Array.isArray(last)) {
    const cols = opts?.columns ?? [];
    const tsIndex = cols.findIndex((c) =>
      ["ts", "timestamp", "timestampms"].includes(c.toLowerCase()),
    );
    if (tsIndex >= 0) {
      const t0 = tupleTimestamp(first, tsIndex);
      const t1 = tupleTimestamp(last, tsIndex);
      if (t0 != null && t1 != null) useTail = t0 <= t1;
    }
  }

  return {
    rows: useTail ? rows.slice(-cap) : rows.slice(0, cap),
    truncated: true,
    rowCount: rows.length,
  };
}
